import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  DefaultPalette,
  DefaultEffects,
  ComboBox,
  Dropdown,
  IDropdownOption,
  DatePicker,
  PrimaryButton,
} from "@fluentui/react";
import { Card } from "@coherence-design-system/controls";
import { CoherenceDataGrid } from "@coherence-design-system/data-grid";
import { appInsights } from "../../services/Logging/AppInsights";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { 
  IQuoteProcessingMetricsResponse,
  QuoteProcessingMetricsService 
} from "../../services/Quotations/QuoteProcessingMetricsService";

interface IQuoteProcessingMetricsProps {
  // Add any props if needed
}

export const QuoteProcessingMetrics: React.FunctionComponent<IQuoteProcessingMetricsProps> = (props) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [metricsData, setMetricsData] = useState<IQuoteProcessingMetricsResponse[]>([]);
  const [filteredData, setFilteredData] = useState<IQuoteProcessingMetricsResponse[]>([]);
  const { userRegions } = useLocalStorageContext();
  
  // Calculate default dates (last one week from 9/1/2025 or today, whichever is later)
  const getDefaultDates = () => {
    const today = new Date();
    const minDate = new Date(2025, 8, 1); // September 1st, 2025 (month is 0-indexed)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);
    
    // Use the later of minDate or oneWeekAgo as the from date
    const fromDate = oneWeekAgo < minDate ? minDate : oneWeekAgo;
    
    return { fromDate, toDate: today, minDate };
  };

  const defaultDates = getDefaultDates();
  
  // Filter states with default values for last one week
  const [quoteIdFilter, setQuoteIdFilter] = useState<string>("");
  const [countryFilter, setCountryFilter] = useState<string>("");
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [quoteStatusFilter, setQuoteStatusFilter] = useState<string>("confirmed");
  const [fromDateFilter, setFromDateFilter] = useState<Date | null>(defaultDates.fromDate);
  const [toDateFilter, setToDateFilter] = useState<Date | null>(defaultDates.toDate);

  useEffect(() => {
    // Track page view
    appInsights.trackPageView({
      name: "QuoteProcessingMetrics",
      uri: "/QuoteProcessingMetrics"
    });
    
    // Initialize the component with default date range
    loadMetricsData(fromDateFilter, toDateFilter);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMetricsData = useCallback(async (fromDate?: Date | null, toDate?: Date | null) => {
    setIsLoading(true);
    
    try {
      // Fetch data from Cosmos DB through the service with date filters
      console.log("Loading quote processing metrics from Cosmos DB");
      console.log("Date filters:", { fromDate, toDate });
      
      const response = await QuoteProcessingMetricsService.getQuoteProcessingMetricsFromCosmos(
        fromDate || undefined, 
        toDate || undefined
      );
      
      // Ensure we have an array - handle different response structures
      let cosmosData: IQuoteProcessingMetricsResponse[] = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          cosmosData = response.data;
        } else {
          // If data is wrapped in another structure, extract it
          cosmosData = response.data.data || [];
        }
      }
      
      console.log("Loaded data:", cosmosData);
      console.log("Data type:", typeof cosmosData, "Is array:", Array.isArray(cosmosData));
      
      // Filter out records without valid userSessionEndTime
      const validData = cosmosData.filter(item => {
        // Keep only records that have a valid quotation number AND valid userSessionEndTime
        if (!item || !item.quotationNumber) return false;
        if (!item.userSessionEndTime) return false;
        const date = new Date(item.userSessionEndTime);
        return !isNaN(date.getTime());
      });
      
      console.log(`Filtered data: ${validData.length} valid records out of ${cosmosData.length} total (${cosmosData.length - validData.length} records excluded due to missing session end time)`);
      
      // Sort data by userSessionEndTime in descending order (latest sessions first)
      const sortedData = validData.sort((a, b) => {
        const dateA = new Date(a.userSessionEndTime!).getTime();
        const dateB = new Date(b.userSessionEndTime!).getTime();
        return dateB - dateA; // Descending order (latest sessions first)
      });
      
      console.log('Data sorted by userSessionEndTime. Sample of first 3 records:');
      console.table(sortedData.slice(0, 3).map(item => ({ 
        quotationNumber: item.quotationNumber, 
        userSessionEndTime: item.userSessionEndTime,
        userSessionStartTime: item.userSessionStartTime 
      })));
      
      setMetricsData(sortedData);
      // Don't set filteredData here - let applyFilters handle it with the current filter state
      
      appInsights.trackEvent({
        name: "QuoteProcessingMetrics data loaded successfully from Cosmos",
        properties: { 
          recordCount: validData.length,
          filteredCount: cosmosData.length - validData.length,
          fromDate: fromDate?.toISOString(),
          toDate: toDate?.toISOString()
        }
      });
    } catch (error) {
      console.error("Error loading quote processing metrics from Cosmos:", error);
      
      // Set empty data when there's an error
      setMetricsData([]);
      setFilteredData([]);
      
      appInsights.trackException({
        error: error as Error,
        properties: {
          name: "Error loading QuoteProcessingMetrics data from Cosmos"
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

    const filterByRegion = (data: IQuoteProcessingMetricsResponse[]) => {
    if (userRegions && userRegions.length > 0) {
      return data.filter(item => item.region && userRegions.includes(item.region));
    }
    return data;
  };
  const applyFilters = useCallback(() => {
    let filtered = filterByRegion(metricsData);

    if (quoteIdFilter.trim()) {
      filtered = filtered.filter(item => 
        item.quotationNumber.toLowerCase().includes(quoteIdFilter.toLowerCase())
      );
    }

    if (countryFilter) {
      filtered = filtered.filter(item => item.country === countryFilter);
    }

    if (regionFilter) {
      filtered = filtered.filter(item => item.region === regionFilter);
    }

    if (quoteStatusFilter) {
      filtered = filtered.filter(item => 
        item.status && item.status.toLowerCase() === quoteStatusFilter.toLowerCase()
      );
    }

    // Note: Date filtering is now done at the database level
    // This function now only applies non-date filters

    // Sort filtered data by userSessionEndTime in descending order (latest sessions first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.userSessionEndTime!).getTime();
      const dateB = new Date(b.userSessionEndTime!).getTime();
      return dateB - dateA; // Descending order (latest sessions first)
    });

    setFilteredData(filtered);
  }, [metricsData, quoteIdFilter, countryFilter, regionFilter, quoteStatusFilter, userRegions]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Apply filters whenever metricsData changes (after data loads)
  useEffect(() => {
    if (metricsData.length > 0) {
      applyFilters();
    }
  }, [metricsData, applyFilters]);

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setQuoteIdFilter("");
    setCountryFilter("");
    setRegionFilter("");
    setQuoteStatusFilter("confirmed");
    setFromDateFilter(defaultDates.fromDate);
    setToDateFilter(defaultDates.toDate);
    
    // Reload data with default date range
    loadMetricsData(defaultDates.fromDate, defaultDates.toDate);
  };

  // Get filtered data based on current region selection for cascading filters
  const getFilteredDataForCascading = () => {
    if (!Array.isArray(metricsData)) {
      return [];
    }
    
    let filteredData = [...metricsData];
    
    // First apply user regions filter if applicable
    if (userRegions && userRegions.length > 0) {
      filteredData = filteredData.filter(item => item.region && userRegions.includes(item.region));
    }
    
    // Then apply selected region filter if set
    if (regionFilter) {
      filteredData = filteredData.filter(item => item.region === regionFilter);
    }
    
    return filteredData;
  };

  const getUniqueQuoteIds = (): IDropdownOption[] => {
    // Use filtered data for cascading effect
    const filteredData = getFilteredDataForCascading();
    
    if (filteredData.length === 0) {
      return [{ key: '', text: 'All Quote IDs' }];
    }
    
    let quoteIds = Array.from(new Set(filteredData.map(item => item.quotationNumber))).sort();
    
    // If there's a search term in quoteIdFilter, filter the options to show matching ones
    if (quoteIdFilter && quoteIdFilter.trim() !== '') {
      const searchTerm = quoteIdFilter.toLowerCase();
      quoteIds = quoteIds.filter(quoteId => 
        quoteId.toLowerCase().includes(searchTerm)
      );
    }
    
    return [
      { key: '', text: 'All Quote IDs' },
      ...quoteIds.map(quoteId => ({ key: quoteId, text: quoteId }))
    ];
  };

  const getUniqueCountries = (): IDropdownOption[] => {
    // Use filtered data for cascading effect - this data is already filtered by user regions
    // in the getFilteredDataForCascading function
    const filteredData = getFilteredDataForCascading();
    
    if (filteredData.length === 0) {
      return [{ key: '', text: 'All Countries' }];
    }
    
    const countries = Array.from(new Set(filteredData.map(item => item.country))).sort();
    console.log('Countries available after filtering:', countries);
    
    return [
      { key: '', text: 'All Countries' },
      ...countries.map(country => ({ key: country, text: country }))
    ];
  };

  const getUniqueRegions = (): IDropdownOption[] => {
    // Regions dropdown should only show regions available to the user
    if (!Array.isArray(metricsData)) {
      console.warn("metricsData is not an array:", metricsData);
      return [{ key: '', text: 'All Regions' }];
    }
    
    console.log('All available regions in data:', 
      Array.from(new Set(metricsData.map(item => item.region).filter(Boolean))));
    console.log('User assigned regions from context:', userRegions);
    
    let regions: string[];
    
    // If user has specific assigned regions, only show those regions
    if (userRegions && userRegions.length > 0) {
      // Filter regions from metricsData that match the user's assigned regions
      regions = Array.from(
        new Set(
          metricsData
            .map(item => item.region)
            .filter(region => region && userRegions.includes(region))
        )
      ).sort();
      
      console.log('Regions after filtering by user regions:', regions);
    } else {
      // Otherwise, show all available regions from the data
      regions = Array.from(
        new Set(
          metricsData
            .map(item => item.region)
            .filter(region => region)
        )
      ).sort();
      
      console.log('All regions (no user region filtering):', regions);
    }
    
    return [
      { key: '', text: 'All Regions' },
      ...regions.map(region => ({ key: region, text: region }))
    ];
  };

  const getUniqueQuoteStatuses = (): IDropdownOption[] => {
    // Show All Statuses, Confirmed and Draft options
    return [
      { key: '', text: 'All Statuses' },
      { key: 'confirmed', text: 'Confirmed' },
      { key: 'draft', text: 'Draft' }
    ];
  };

  const handleRegionChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    const newRegion = option?.key as string || "";
    setRegionFilter(newRegion);
    
    // Clear dependent filters when region changes
    if (newRegion !== regionFilter) {
      setCountryFilter("");
      setQuoteIdFilter("");
      // Don't clear quote status filter as it should be independent
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return "0";
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return "0";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Calculate averages only (without adding to grid data)
  // Note: totalQuoteProcessTime should equal fillQuoteDetails + cartPrepration + quoteCreationDuration (Calculate Quote)
  const calculateAverages = () => {
    if (!filteredData || filteredData.length === 0) {
      return {
        fillQuoteDetails: 0,
        cartPrepration: 0,
        quoteCreationDuration: 0,
        totalQuoteProcessTime: 0,
        totalSessionTimeinSeconds: 0,
        totalCumulativeEditProcessTime: 0,
        editQuoteCount: 0,
        consolidatedTotalTimeSeconds: 0,
        consolidatedAwayTimeSeconds: 0,
        consolidatedIdleTimeSeconds: 0,
        consolidatedProductiveTimeSeconds: 0
      };
    }

    const validData = filteredData.filter(item => item !== null && item !== undefined);
    const count = validData.length;

    if (count === 0) {
      return {
        fillQuoteDetails: 0,
        cartPrepration: 0,
        quoteCreationDuration: 0,
        totalQuoteProcessTime: 0,
        totalSessionTimeinSeconds: 0,
        totalCumulativeEditProcessTime: 0,
        editQuoteCount: 0,
        consolidatedTotalTimeSeconds: 0,
        consolidatedAwayTimeSeconds: 0,
        consolidatedIdleTimeSeconds: 0,
        consolidatedProductiveTimeSeconds: 0
      };
    }

    return {
      fillQuoteDetails: validData.reduce((sum, item) => sum + (item.fillQuoteDetails || 0), 0) / count,
      cartPrepration: validData.reduce((sum, item) => sum + (item.cartPrepration || 0), 0) / count,
      quoteCreationDuration: validData.reduce((sum, item) => sum + (item.quoteCreationDuration || 0), 0) / count,
      totalQuoteProcessTime: validData.reduce((sum, item) => sum + ((item.totalQuoteProcessTime || 0) + (item.totalCumulativeEditProcessTime || 0)), 0) / count,
      totalSessionTimeinSeconds: validData.reduce((sum, item) => sum + (item.totalSessionTimeinSeconds || 0), 0) / count,
      totalCumulativeEditProcessTime: validData.reduce((sum, item) => sum + (item.totalCumulativeEditProcessTime || 0), 0) / count,
      editQuoteCount: validData.reduce((sum, item) => sum + (item.editQuoteCount || 0), 0) / count,
      consolidatedTotalTimeSeconds: validData.reduce((sum, item) => sum + (item.consolidatedTotalTimeSeconds || 0), 0) / count,
      consolidatedAwayTimeSeconds: validData.reduce((sum, item) => sum + (item.consolidatedAwayTimeSeconds || 0), 0) / count,
      consolidatedIdleTimeSeconds: validData.reduce((sum, item) => sum + (item.consolidatedIdleTimeSeconds || 0), 0) / count,
      consolidatedProductiveTimeSeconds: validData.reduce((sum, item) => sum + (item.consolidatedProductiveTimeSeconds || 0), 0) / count
    };
  };

  // Helper to navigate to QuotationDetail page for a given quoteId
  const navigateToQuotationDetail = (quoteId: string) => {
    if (!quoteId) return;
    // Find the full record for the selected quoteId
    const record = metricsData.find(item => item.quotationNumber === quoteId);
    if (record) {
      const id = record.id;
      const partitionKey = record.partitionKey;
      navigate('/QuotationDetail', { state: { searchText: id, partitionKey } });
    }
  };

  const gridColumns = [
    {
      fieldName: "quotationNumber",
      key: "quotationNumber",
      name: "Quote #",
      type: "string",
      isResizable: true,
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: IQuoteProcessingMetricsResponse) => (
        <button
          style={{ textDecoration: 'underline', color: DefaultPalette.themePrimary, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'normal' }}
          onClick={e => {
            e.preventDefault();
            navigateToQuotationDetail(item.quotationNumber);
          }}
        >
          {item.quotationNumber}
        </button>
      ),
    },
    {
      fieldName: "country",
      key: "country", 
      name: "Country",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
    },
    {
      fieldName: "userSessionStartTime",
      key: "userSessionStartTime",
      name: "Session Start Time",
      type: "string",
      isResizable: true,
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: IQuoteProcessingMetricsResponse) => {
        if (!item.userSessionStartTime) return "N/A";
        const date = new Date(item.userSessionStartTime);
        if (isNaN(date.getTime())) return "N/A";
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
    },
    {
      fieldName: "fillQuoteDetails",
      key: "fillQuoteDetails",
      name: "Fill Quote",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.fillQuoteDetails),
    },
    {
      fieldName: "cartPrepration",
      key: "cartPrepration",
      name: "Prepare Cart",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.cartPrepration),
    },
    {
      fieldName: "quoteCreationDuration",
      key: "quoteCreationDuration",
      name: "Calculate Quote",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.quoteCreationDuration),
    },
    {
      fieldName: "totalCumulativeEditProcessTime",
      key: "totalCumulativeEditProcessTime",
      name: "Quote Edit",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.totalCumulativeEditProcessTime),
    },
    {
      fieldName: "totalQuoteProcessTime",
      key: "totalQuoteProcessTime",
      name: "Total Quote Process Time",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: IQuoteProcessingMetricsResponse) => {
        const totalProcessTime = (item.totalQuoteProcessTime || 0) + (item.totalCumulativeEditProcessTime || 0);
        return formatDuration(totalProcessTime);
      },
      // Note: Total Quote Process Time = Initial Process Time + All Edit Process Time
    },
    {
      fieldName: "consolidatedTotalTimeSeconds",
      key: "consolidatedTotalTimeSeconds",
      name: "Total Session Time",
      type: "string",
      isResizable: true,
      minWidth: 65,
      maxWidth: 85,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.consolidatedTotalTimeSeconds),
    },
    {
      fieldName: "editQuoteCount",
      key: "editQuoteCount",
      name: "Quote Edited(Number Of Times)",
      type: "string",
      isResizable: true,
      minWidth: 40,
      maxWidth: 60,
      onRender: (item: IQuoteProcessingMetricsResponse) => item.editQuoteCount?.toString() || "0",
    },
    {
      fieldName: "consolidatedAwayTimeSeconds",
      key: "consolidatedAwayTimeSeconds",
      name: "Away Time",
      type: "string",
      isResizable: true,
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.consolidatedAwayTimeSeconds),
    },
    {
      fieldName: "consolidatedIdleTimeSeconds",
      key: "consolidatedIdleTimeSeconds",
      name: "Idle Time",
      type: "string",
      isResizable: true,
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.consolidatedIdleTimeSeconds),
    },
    {
      fieldName: "consolidatedProductiveTimeSeconds",
      key: "consolidatedProductiveTimeSeconds",
      name: "Productive Time",
      type: "string",
      isResizable: true,
      minWidth: 75,
      maxWidth: 95,
      onRender: (item: IQuoteProcessingMetricsResponse) => formatDuration(item.consolidatedProductiveTimeSeconds),
    },
    {
      fieldName: "totalTabsCount",
      key: "totalTabsCount",
      name: "Tabs Switched",
      type: "string",
      isResizable: true,
      minWidth: 40,
      maxWidth: 55,
      onRender: (item: IQuoteProcessingMetricsResponse) => item.totalTabsCount?.toString() || "0",
    },
    {
      fieldName: "userSessionEndTime",
      key: "userSessionEndTime",
      name: "Session End Time",
      type: "string",
      isResizable: true,
      minWidth: 100,
      maxWidth: 120,
      isSorted: true,
      isSortedDescending: true,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      onRender: (item: IQuoteProcessingMetricsResponse) => {
        const date = new Date(item.userSessionEndTime!);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
    },
    {
      fieldName: "createdByName",
      key: "createdByName",
      name: "Created By",
      type: "string",
      isResizable: true,
      minWidth: 80,
      maxWidth: 120,
    },
    {
      fieldName: "status",
      key: "status",
      name: "Quote Status",
      type: "string",
      isResizable: true,
      minWidth: 55,
      maxWidth: 70,
    },
  ] as any;

  const cardStyles = {
    root: {
      marginLeft: "16px",
      marginRight: "16px",
      marginTop: "0px",
      marginBottom: "0px",
      padding: "8px",
      maxWidth: "none",
      width: "calc(100% - 32px)"
    }
  };

  // WCAG AA compliant color for average labels (4.54:1 contrast on #edebe9 background)
  const averageLabelColor = '#006cbe';

  const headerStyles = {
    root: {
      marginBottom: "0px",
      padding: "4px 0",
      marginLeft: "16px",
      marginRight: "16px"
    }
  };

  return (
    <Stack
      tokens={{ childrenGap: 2 }}
      styles={{
        root: {
          padding: "12px 12px 0 0",
          height: "100vh",
          backgroundColor: DefaultPalette.neutralLighter
        }
      }}
    >
      {/* Page Header */}
      <Stack horizontal styles={headerStyles} verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text 
          variant="xxLarge" 
          styles={{ 
            root: { 
              fontWeight: 600, 
              color: DefaultPalette.themePrimary,
              marginBottom: "8px"
            } 
          }}
        >
          Quote Processing Metrics Report
        </Text>
        <span style={{ flex: 1 }} />
        <PrimaryButton
          text="Export to Excel"
          style={{ marginTop: 0, marginBottom: 0, width: 180 }}
          disabled={!filteredData || filteredData.length === 0}
          onClick={() => {
            if (!filteredData || filteredData.length === 0) {
              return;
            }
            // Prepare data for export with null/undefined handling and formatted durations
            const exportData = filteredData.map(item => ({
              "Quote #": item.quotationNumber || "N/A",
              "Country": item.country || "N/A",
              "Session Start Time": item.userSessionStartTime ? new Date(item.userSessionStartTime).toLocaleString() : "N/A",
              "Fill Quote": formatDuration(item.fillQuoteDetails),
              "Prepare Cart": formatDuration(item.cartPrepration),
              "Calculate Quote": formatDuration(item.quoteCreationDuration),
              "Quote Edit": formatDuration(item.totalCumulativeEditProcessTime),
              "Total Quote Process Time": formatDuration((item.totalQuoteProcessTime || 0) + (item.totalCumulativeEditProcessTime || 0)),
              "Total Session Time": formatDuration(item.consolidatedTotalTimeSeconds),
              "Quote Edited (Times)": item.editQuoteCount?.toString() || "0",
              "Away Time": formatDuration(item.consolidatedAwayTimeSeconds),
              "Idle Time": formatDuration(item.consolidatedIdleTimeSeconds),
              "Productive Time": formatDuration(item.consolidatedProductiveTimeSeconds),
              "Tabs Switched": item.totalTabsCount?.toString() || "0",
              "Session End Time": item.userSessionEndTime ? new Date(item.userSessionEndTime).toLocaleString() : "N/A",
              "Created By": item.createdByName || "N/A",
              "Quote Status": item.status || "N/A"
            }));
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "QuoteProcessingMetrics");
            const now = new Date();
            const pad = (n: number, len = 2): string => n.toString().padStart(len, '0');
            const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
            XLSX.writeFile(workbook, `QuoteProcessingMetrics_${timestamp}.xlsx`);
          }}
        />
      </Stack>

      {/* Filter Controls */}
      <Stack
        horizontal
        tokens={{ childrenGap: 16 }}
        styles={{
          root: {
            padding: "16px",
            backgroundColor: DefaultPalette.white,
            borderRadius: "4px",
            boxShadow: DefaultEffects.elevation4,
            marginLeft: "16px",
            marginRight: "16px",
            marginTop: "0px",
            marginBottom: "0px"
          }
        }}
      >
        <ComboBox
          placeholder="Type to search Quote ID..."
          ariaLabel="Search Quote ID"
          selectedKey={quoteIdFilter}
          onChange={(_, option) => setQuoteIdFilter(option?.key as string || "")}
          onInputValueChange={(value) => {
            // Update the filter as user types
            setQuoteIdFilter(value || "");
          }}
          options={getUniqueQuoteIds()}
          allowFreeform={true}
          autoComplete="on"
          useComboBoxAsMenuWidth={true}
          styles={{ 
            root: { width: 200 },
            input: { fontSize: 14 }
          }}
          calloutProps={{
            styles: {
              root: {
                maxHeight: 200,
                overflowY: 'auto'
              }
            }
          }}
        />
        
        <Dropdown
          placeholder="Select Country"
          ariaLabel="Select Country"
          selectedKey={countryFilter}
          onChange={(_, option) => setCountryFilter(option?.key as string || "")}
          options={getUniqueCountries()}
          styles={{ root: { width: 150 } }}
        />
        
        <Dropdown
          placeholder="Select Region"
          ariaLabel="Select Region"
          selectedKey={regionFilter}
          onChange={handleRegionChange}
          options={getUniqueRegions()}
          styles={{ root: { width: 150 } }}
        />
        
        <Dropdown
          placeholder="Select Quote Status"
          ariaLabel="Select Quote Status"
          selectedKey={quoteStatusFilter}
          onChange={(_, option) => setQuoteStatusFilter(option?.key as string || "")}
          options={getUniqueQuoteStatuses()}
          styles={{ root: { width: 150 } }}
        />
        
        <DatePicker
          placeholder="From Date"
          ariaLabel="From Date"
          value={fromDateFilter || undefined}
          onSelectDate={(date) => {
            setFromDateFilter(date || null);
            // Automatically reload data when date changes
            if (date !== fromDateFilter) {
              loadMetricsData(date || null, toDateFilter);
            }
          }}
          styles={{ root: { width: 140 } }}
          minDate={defaultDates.minDate}
          maxDate={toDateFilter || undefined}
        />
        
        <DatePicker
          placeholder="To Date"
          ariaLabel="To Date"
          value={toDateFilter || undefined}
          onSelectDate={(date) => {
            setToDateFilter(date || null);
            // Automatically reload data when date changes
            if (date !== toDateFilter) {
              loadMetricsData(fromDateFilter, date || null);
            }
          }}
          styles={{ root: { width: 140 } }}
          minDate={fromDateFilter || undefined}
        />
        
        <PrimaryButton
          text="Clear Filters"
          onClick={clearFilters}
          disabled={!quoteIdFilter && !countryFilter && !regionFilter && quoteStatusFilter === "confirmed" &&
            (!fromDateFilter || fromDateFilter.getTime() === defaultDates.fromDate.getTime()) && 
            (!toDateFilter || toDateFilter.getTime() === defaultDates.toDate.getTime())}
        />
      </Stack>

      {/* Filtered Quote Count */}
      <div style={{ marginLeft: "auto",marginRight: 15, marginTop: 0, marginBottom: 0, fontSize: 14, color: '#333' }}>
        Showing <b>{filteredData.length}</b> quote{filteredData.length === 1 ? '' : 's'}
      </div>

      {/* Main Data Grid */}
      <Card styles={cardStyles}>
        <Stack tokens={{ childrenGap: 16 }}>
          {isLoading ? (
            <Stack 
              horizontalAlign="center" 
              verticalAlign="center" 
              styles={{ root: { minHeight: "400px" } }}
            >
              <Spinner 
                size={SpinnerSize.large} 
                label="Loading quote processing metrics..." 
              />
            </Stack>
          ) : (
            <Stack tokens={{ childrenGap: 12 }}>
              {filteredData.length > 0 ? (
                <>
                  <div style={{ 
                    width: '100%',
                    height: 'calc(100vh - 420px)',
                    border: `1px solid ${DefaultPalette.neutralQuaternary}`,
                    borderRadius: "4px 4px 0 0",
                    borderBottom: "none",
                    overflow: 'auto'
                  }}>
                    <style>{`
                      [data-automation-id="DetailsList"] .ms-DetailsHeader {
                        position: sticky !important;
                        top: 0 !important;
                        z-index: 10 !important;
                        background-color: ${DefaultPalette.neutralLighterAlt} !important;
                        border-bottom: 1px solid ${DefaultPalette.neutralQuaternary} !important;
                      }
                      [data-automation-id="DetailsList"] .ms-DetailsHeader-cell {
                        background-color: ${DefaultPalette.neutralLighterAlt} !important;
                      }
                      [data-automation-id="DetailsList"] .ms-DetailsRow-cell {
                        padding-left: 12px !important;
                        padding-right: 8px !important;
                      }
                      [data-automation-id="DetailsList"] .ms-DetailsHeader-cell {
                        padding-left: 12px !important;
                        padding-right: 8px !important;
                      }
                    `}</style>
                    <CoherenceDataGrid
                      ariaLabelForSelectAllCheckbox="select all checkbox"
                      checkButtonAriaLabel="checkbox"
                      compactMode="compact"
                      data={filteredData}
                      isLoading={false}
                      isScrollable={false}
                      isSortable={true}
                      listConfig={gridColumns}
                      selectionMode={0}
                      setKey="QUOTE_PROCESSING_METRICS_DATA_GRID"
                      constrainMode={1}
                      layoutMode={1}
                    />
                  </div>
                  
                  {/* Averages Section - redesigned for clear, even spacing */}
                  <div
                    style={{
                      width: '100%',
                      backgroundColor: DefaultPalette.neutralLight,
                      border: `1px solid ${DefaultPalette.neutralQuaternary}`,
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      fontSize: '13px',
                      fontFamily: 'Segoe UI, sans-serif',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 24px',
                      minHeight: '44px',
                      gap: '18px',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', color: DefaultPalette.themeDark, minWidth: 90 }}>AVERAGES</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', width: '100%' }}>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Fill Quote Details:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().fillQuoteDetails)}</span>
                      </div>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Cart Preparation:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().cartPrepration)}</span>
                      </div>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Calculate Quote:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().quoteCreationDuration)}</span>
                      </div>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Quote Edit:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().totalCumulativeEditProcessTime)}</span>
                      </div>
                      <div style={{ minWidth: 150, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Total Process Time:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().totalQuoteProcessTime)}</span>
                      </div>
                      <div style={{ minWidth: 150, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Total Session Time:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().consolidatedTotalTimeSeconds)}</span>
                      </div>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Away Time:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().consolidatedAwayTimeSeconds)}</span>
                      </div>
                      <div style={{ minWidth: 130, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Idle Time:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().consolidatedIdleTimeSeconds)}</span>
                      </div>
                      <div style={{ minWidth: 150, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: averageLabelColor, fontWeight: 600 }}>Productive Time:</span>
                        <span style={{ fontWeight: 600 }}>{formatDuration(calculateAverages().consolidatedProductiveTimeSeconds)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes Section */}
                  <div style={{
                    width: '100%',
                    backgroundColor: DefaultPalette.neutralLighterAlt,
                    border: `1px solid ${DefaultPalette.neutralQuaternary}`,
                    borderTop: "none",
                    borderRadius: "0 0 4px 4px",
                    padding: "12px 16px",
                    fontSize: "11px",
                    fontFamily: "Segoe UI, sans-serif",
                    color: DefaultPalette.neutralDark
                  }}>
                    <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "12px" }}>Notes:</div>
                    <div style={{ lineHeight: "1.4" }}>
                      <div style={{ marginBottom: "3px" }}>
                        <strong>Total Session Time:</strong> Time from when user starts working on quote until completion
                      </div>
                      <div style={{ marginBottom: "3px" }}>
                        <strong>Idle Time:</strong> Time when user has no interactions for &gt;30 seconds
                      </div>
                      <div style={{ marginBottom: "3px" }}>
                        <strong>Away Time:</strong> Time when user switches to other tabs or windows
                      </div>
                      <div>
                        <strong>Productive Time:</strong> Only the time when user is actively working
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <Stack 
                  horizontalAlign="center" 
                  verticalAlign="center"
                  styles={{ root: { minHeight: "300px" } }}
                >
                  <Text variant="medium" styles={{ root: { color: DefaultPalette.neutralSecondary } }}>
                    {metricsData.length === 0 
                      ? "No quote processing metrics data available" 
                      : "No data matches the current filter criteria"}
                  </Text>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default QuoteProcessingMetrics;
